import { AfterViewInit, Component, computed, effect, inject, input, Input, LOCALE_ID, signal } from '@angular/core';
import { commonImports } from '../../common';
import { UserOrganizationsService } from '../../services/user-organizations';
import { TokenService } from '../../services/auth-token-service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { signOut } from '@aws-amplify/auth';
import { selectedOrganizationIdStore } from '../../services/global-storage';

@Component({
  selector: 'app-main-view',
  imports: [...commonImports],
  templateUrl: './main-view.component.html',
  styleUrl: './main-view.component.scss',
})
export class MainViewComponent implements AfterViewInit {
  readonly orgId = input<string | null>(null);

  readonly selectedOrganizationId = signal<string | null>(null);

  readonly orgIdNavigated = effect(() => {
    if (this.orgId() != null) {
      this.changeOrganizationGlobal(this.orgId()!);
    }
  });

  readonly selectedOrganization = computed(() => {
    const orgId = this.selectedOrganizationId();
    const orgs = this.userOrganizationsService.cachedOrganizations();
    const foundOrganization = orgs?.find((org) => org.orgId === orgId);

    return foundOrganization;
  });

  readonly disableOrganizationsResources = computed(() => this.selectedOrganization() == null);

  @Input() skipNavigation: boolean = false;

  public readonly userEmail = signal<string>('');

  private readonly localeId = inject(LOCALE_ID);

  constructor(
    public readonly userOrganizationsService: UserOrganizationsService,
    readonly tokenService: TokenService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        map((e) => e as NavigationEnd),
      )
      .subscribe((e) => {
        const unprotectedPathsStarts = ['/auth-response', '/public/', '/auth/'];

        if (!unprotectedPathsStarts.some((path) => e.url.startsWith(path))) {
          this.tokenService.isUserLoggedIn().then((loggedIn) => {
            if (!loggedIn) {
              this.router.navigate(['/auth/login']);
            }
          });
        }
      });
  }

  async ngAfterViewInit() {
    this.tokenService.userLoggedIn.subscribe((loggedIn) => {
      if (loggedIn) {
        this.tokenService.getIdTokenHolder().then((holder) => {
          const payload = holder.payload as any;
          this.userEmail.set(payload.email ?? '');
          this.userOrganizationsService.refreshCachedOrganizations();
        });
      }
    });
    this.tokenService.isUserLoggedIn();
    selectedOrganizationIdStore.subscribe((orgId) => this.selectedOrganizationId.set(orgId));
  }

  switchLang(targetLocale: string) {
    const currentLocale = this.localeId;
    const currentPath = window.location.pathname;
    if (currentLocale != null && currentPath.startsWith(`/${currentLocale}/`)) {
      const path = currentPath.replace(`/${currentLocale}/`, `/${targetLocale}/`);
      window.location.pathname = path;
    } else {
      window.location.href = `/${targetLocale}`;
    }
  }

  changeOrganizationGlobal(orgId: string) {
    selectedOrganizationIdStore.next(orgId);
  }

  formatOrgRoute(pathRest: string) {
    return `/organizations/${this.selectedOrganization()?.orgId}/${pathRest}`;
  }

  async logout() {
    await signOut();
    this.router.navigate(['/auth/login']);
  }

  isTestVersion() {
    const host = window.location.hostname;
    return (
      host.startsWith('dev.') ||
      host.startsWith('test.') ||
      host.startsWith('localhost') ||
      host.startsWith('127.0.0.1')
    );
  }
}
